import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  disabled?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  disabled = false
}) => {
  if (!isOpen) return null;

  const getTypeConfig = () => {
    switch (type) {
      case 'danger':
        return {
          iconClass: 'text-red-600',
          bgClass: 'bg-red-100',
          buttonClass: 'bg-red-600 hover:bg-red-700 text-white',
          icon: AlertTriangle
        };
      case 'warning':
        return {
          iconClass: 'text-yellow-600',
          bgClass: 'bg-yellow-100',
          buttonClass: 'bg-yellow-600 hover:bg-yellow-700 text-white',
          icon: AlertTriangle
        };
      case 'info':
        return {
          iconClass: 'text-blue-600',
          bgClass: 'bg-blue-100',
          buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
          icon: AlertTriangle
        };
      default:
        return {
          iconClass: 'text-red-600',
          bgClass: 'bg-red-100',
          buttonClass: 'bg-red-600 hover:bg-red-700 text-white',
          icon: AlertTriangle
        };
    }
  };

  const config = getTypeConfig();
  const IconComponent = config.icon;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-[var(--modal-background)] rounded-lg shadow-xl max-w-md w-full mx-4 animate-scale-in">
        {/* Header */}
        <div className="px-6 pt-4">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors block ml-auto"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 text-center">
          {/* Icon */}
          <div className={`mx-auto mb-4 w-16 h-16 ${config.bgClass} rounded-full flex items-center justify-center`}>
            <IconComponent className={`h-8 w-8 ${config.iconClass}`} />
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-[var(--color-text)] mb-2">
            {title}
          </h3>

          {/* Message */}
          <p className="text-[var(--color-sub-title-text)] mb-6">
            {message}
          </p>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={disabled}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-[var(--color-sub-title-text)] hover:bg-[var(--button-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={disabled}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${config.buttonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
